import ToolTip from 'core/components/Tooltip';
import React, {
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useSelectableContext,
  useTableReset,
} from 'core/hooks';
import {
  mediaEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  generateDropzoneAccept,
} from 'core/mimeMap';
import UploadIcon from 'core/icons/upload';
import Table from 'core/components/VirtualTable';
import {
  MultiMediaResponse,
} from 'dtos/multiMediaResponse';
import {
  VirtualTableInstance,
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  getPatientsMultimedia,
} from 'pages/Dashboard/services/api';
import {
  useGetMediaSettings,
} from 'pages/Dashboard/hooks/useUploadPatientMedia';
import usePreviewMediaClick from 'pages/Dashboard/hooks/usePreviewMediaClick';
import IconButton from 'core/components/IconButton';
import PreviewOutlined from '@mui/icons-material/PreviewOutlined';
import CloseIcon from '@mui/icons-material/Close';
import Empty from 'core/components/Empty';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import Spinner from 'pages/Dashboard/components/Spinner';
import noop from 'lodash/noop';
import SelectableProvider from 'core/context/selectable';
import isNumber from 'lodash/isNumber';
import head from 'lodash/head';
import {
  Row,
} from '@tanstack/react-table';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import TabFilter from 'views/EMR/components/TabFilter';
import {
  useGetMediaCategoryTabOptions,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/hooks';
import {
  useResolvePatientMultimedia,
} from 'views/EMR/hooks/media';
import compact from 'lodash/compact';
import Button from 'pages/Dashboard/components/Button';

const supportedUploadFileTypes = 'jpeg, jpg, pdf, png, tiff, tif';
const dropzoneAccept = generateDropzoneAccept(supportedUploadFileTypes.split(','));

type CellProps = {
  row: Row<MultiMediaResponse>;
};

type Parts = 'root' | 'table';

type Props = {
  patientId: number;
  offsetHeight: number;
  multiple?: boolean;
  disabled?: boolean;
  value?: null | number | number[];
  onChange: (value: number | number[]) => void;
  onSelectedMediaChange?: (mediaList: MultiMediaResponse[]) => void;
  onMediaDoubleClick?: (mediaId: number) => void;
  disabledMediaIds?: number[];
  disabledReason?: string;
  className?: string;
  classes?: Partial<Record<Parts, string>>;
  enableTabFilter?: boolean;
  allowedMimeTypes?: string[];
  allowedMimeTypeReason?: string;
  isLoading?: boolean;
};

function FileCheckbox({ row }: CellProps) {
  const { isSelected } = useSelectableContext();
  const checked = isSelected(row.id);
  return (
    <Checkbox
      color="secondary"
      id={row.id}
      checked={checked}
      onChange={noop}
    />
  );
}

function FileNameCell({ row }: CellProps) {
  return (
    <div className="truncate text-gray-600 font-medium flex-1 min-w-0">
      <ToolTip title={row.original.fileName}>
        <span>
          {row.original.fileName}
        </span>
      </ToolTip>
    </div>
  );
}

function MediaTypeCell({ row }: CellProps) {
  const { mediaTypeDisplayNameLookUp = {} } = useGetMediaSettings();
  const { multimediaType } = row.original ?? {};
  const displayName = mediaTypeDisplayNameLookUp?.[multimediaType!] ?? multimediaType;

  return (
    <div className="truncate text-gray-600 font-medium ">
      <ToolTip title={displayName}>
        <span className="truncate">
          {displayName}
        </span>
      </ToolTip>
    </div>
  );
}

function PreviewCell({ row }: CellProps) {
  const handleTableRowClick = usePreviewMediaClick();

  return (
    <div className="ml-auto">
      <IconButton
        tooltip="Preview"
        onClick={(e) => {
          e.stopPropagation();
          handleTableRowClick(row.original);
        }}
      >
        <PreviewOutlined className="!w-4 !h-4 text-gray-400" />
      </IconButton>
    </div>
  );
}

const fileNameColumn: ColumnDef<MultiMediaResponse> = {
  id: 'fileName',
  accessorKey: 'fileName',
  header: 'Name',
  meta: { autoSized: true },
  cell: ({ row }) => (
    <FileNameCell row={row} />
  ),
};

const fileNameWithCheckboxColumn: ColumnDef<MultiMediaResponse> = {
  ...fileNameColumn,
  cell: ({ row }) => (
    <div className="flex flex-row gap-2 truncate">
      <FileCheckbox row={row} />
      <FileNameCell row={row} />
    </div>
  ),
};

export const columns: ColumnDef<MultiMediaResponse>[] = [
  {
    id: 'type',
    accessorKey: 'multimediaType',
    header: 'Type',
    size: 120,
    cell: ({ row }) => <MediaTypeCell row={row} />,
  }, {
    id: 'action2',
    accessorKey: 'preview',
    header: '',
    size: 80,
    cell: ({ row }) => <PreviewCell row={row} />,
  },
];

type SelectedMedia = {
  mediaId: number;
  fileName: string;
};

type SelectedMediaListProps = {
  mediaList: SelectedMedia[];
  onUnselect: (mediaId: number) => void;
};

function SelectedMediaList({ mediaList, onUnselect }: SelectedMediaListProps) {
  return (
    <div className="flex w-72 max-h-56 flex-col gap-1 overflow-y-auto">
      {mediaList.map(({ mediaId, fileName }) => (
        <div
          key={mediaId}
          className="flex items-center gap-2 px-2 py-1  bg-blue-100 text-blue-500 border border-blue-200 rounded-full"
        >
          <span className="flex-1 truncate text-xs font-semibold text-gray-700">
            {fileName}
          </span>
          <IconButton
            tooltip="Unselect"
            size="small"
            className="!p-0.5"
            onClick={() => onUnselect(mediaId)}
          >
            <CloseIcon className="!h-3.5 !w-3.5 text-red-500" />
          </IconButton>
        </div>
      ))}
    </div>
  );
}

function PatientMediaList({
  patientId,
  offsetHeight,
  multiple = true,
  disabled = false,
  value,
  disabledMediaIds = [],
  disabledReason,
  className,
  classes,
  onChange,
  onSelectedMediaChange,
  onMediaDoubleClick,
  enableTabFilter,
  allowedMimeTypes = [],
  allowedMimeTypeReason,
  isLoading = false,
}: Props) {
  const { tableInstanceRef } = useTableReset();
  const { selected, add, remove, isSelected, clear } = useSelectableContext();
  const tabOptions = useGetMediaCategoryTabOptions();
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [pendingMediaIds, setPendingMediaIds] = useState<number[]>([]);
  const selectedMediaIds = useMemo(
    () => Array.from(selected).map(Number),
    [selected],
  );
  const mediaById = useRef<Record<number, MultiMediaResponse>>({});
  const mediaIdsToResolve = useMemo(
    () => [
      ...new Set([
        ...pendingMediaIds,
        ...selectedMediaIds.filter((mediaId) => isNil(mediaById.current[mediaId])),
      ]),
    ],
    [pendingMediaIds, selectedMediaIds],
  );

  const {
    validMediaIds: resolvedPendingMediaIds,
    mediaList: resolvedMediaList,
    isLoading: isResolvingSelectedMedia,
  } = useResolvePatientMultimedia({
    mediaIds: mediaIdsToResolve,
    allowedMimeTypes,
  });
  const isSelectionLoading = isLoading || isResolvingSelectedMedia;
  const isBlockingResolve = isResolvingSelectedMedia
    && (pendingMediaIds.length > 0 || !isNil(onSelectedMediaChange));
  const resolvedMediaById = useMemo(
    () => {
      const mediaById = new Map<number, MultiMediaResponse>();
      resolvedMediaList.forEach((media) => {
        if (isNumber(media.mediaId)) {
          mediaById.set(media.mediaId, media);
        }
      });
      return mediaById;
    },
    [resolvedMediaList],
  );
  const selectedMediaList = useMemo(
    () => selectedMediaIds.map((mediaId) => ({
      mediaId,
      fileName: (resolvedMediaById.get(mediaId) ?? mediaById.current[mediaId])?.fileName ?? 'Loading…',
    })),
    [resolvedMediaById, selectedMediaIds],
  );

  const handleUnselectMedia = (mediaId: number) => {
    remove(mediaId);
    delete mediaById.current[mediaId];
  };

  useEffect(() => {
    resolvedMediaList.forEach((media) => {
      if (isNumber(media.mediaId)) {
        mediaById.current[media.mediaId] = media;
      }
    });
  }, [resolvedMediaList]);

  const validPendingMediaIds = useMemo(
    () => resolvedPendingMediaIds.filter((mediaId) => pendingMediaIds.includes(mediaId)),
    [pendingMediaIds, resolvedPendingMediaIds],
  );

  useEffect(() => {
    onChange(selectedMediaIds);
  }, [onChange, selectedMediaIds]);

  useEffect(() => {
    if (isNil(value)) {
      clear();
    }
  }, [clear, value]);

  const resolvedSelectedMedia = useMemo(() => {
    if (selectedMediaIds.length === 0) {
      return [];
    }

    const mediaList = selectedMediaIds.map(
      (mediaId) => resolvedMediaById.get(mediaId) ?? mediaById.current[mediaId],
    );

    return mediaList.some(isNil) ? undefined : mediaList;
  }, [resolvedMediaById, selectedMediaIds]);

  useEffect(() => {
    if (isNil(onSelectedMediaChange) || isNil(resolvedSelectedMedia)) {
      return;
    }

    onSelectedMediaChange(resolvedSelectedMedia);
  }, [onSelectedMediaChange, resolvedSelectedMedia]);

  const addToSelected = (mediaIds: number[]) => {
    if (multiple) {
      add(...mediaIds);
    } else if (!isNil(head(mediaIds))) {
      add(head(mediaIds)!);
    }
    setPendingMediaIds([]);
    tableInstanceRef?.current?.refetch?.();
  };

  useEffect(() => {
    const shouldResolvePendingSelection = pendingMediaIds.length > 0 && !isResolvingSelectedMedia;

    if (shouldResolvePendingSelection) {
      addToSelected(validPendingMediaIds);
    }
  }, [add, isResolvingSelectedMedia, multiple, pendingMediaIds.length, validPendingMediaIds]);

  const handleUploadMedia = () => {
    mediaEventChannel.emit('onUploadMediaToCharts', {
      dropzoneAccept,
      patientId: patientId ?? 0,
      dialogHeight: offsetHeight,
      defaultCategory: selectedCategory,
      hasBackdrop: false,
      onUpdate: async (newMediaList) => {
          const ids = newMediaList.map((media) => media.patientMultiMediaId).filter(isNumber);
          setPendingMediaIds(ids);
        },
    });
  };

  const fileColumns = useMemo(
    () => (multiple ? [fileNameWithCheckboxColumn, ...columns] : [fileNameColumn, ...columns]),
    [multiple],
  );

  const disabledMediaIdsSet = useMemo(() => new Set(disabledMediaIds), [disabledMediaIds]);
  const allowedMimeTypesSet = useMemo(() => new Set(allowedMimeTypes ?? []), [allowedMimeTypes]);

  const isMimeTypeDisabled = (row: MultiMediaResponse) => (
    allowedMimeTypesSet.size > 0 && !allowedMimeTypesSet.has(row.mimeType ?? '')
  );

  const isRowDisabled = (row: MultiMediaResponse) => (
    disabledMediaIdsSet.has(row.mediaId ?? 0) || isMimeTypeDisabled(row)
  );

  const handleRowSelect = (media: MultiMediaResponse) => {
    const mediaId = media.mediaId ?? 0;
    const isDisabledMedia = disabledMediaIdsSet.has(mediaId);

    if (isDisabledMedia) {
      return;
    }

    mediaById.current[mediaId] = media;

    if (isSelected(mediaId) && multiple) {
      remove(mediaId);
      delete mediaById.current[mediaId];
    } else {
      add(mediaId);
    }
  };

  return (
    <div className={clsx('h-full flex flex-col gap-2', className, classes?.root)}>
      <div className="flex flex-col gap-4 font-semibold text-xs">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-1.5">
            <div className="text-gray-700 font-semibold text-sm">
              Patient Documents
            </div>
            {multiple && selectedMediaIds.length > 0 && (
              <>
                <span className="text-gray-400">•</span>
                <ToolTip
                  isInteractive
                  classes={{ tooltip: '!p-2 !max-w-none' }}
                  title={(
                    <SelectedMediaList
                      mediaList={selectedMediaList}
                      onUnselect={handleUnselectMedia}
                    />
                  )}
                >
                  <span className="cursor-default font-semibold text-blue-500 border border-blue-200 bg-blue-50 px-2 py-1 rounded-full">
                    {`${selectedMediaIds.length} Selected`}
                  </span>
                </ToolTip>
              </>
            )}
          </div>
          <ToolTip
            title={(
              <p>
                Attach and Upload to Chart
                <br />
                Supported file types:
                <br />
                <strong className="font-semibold">
                  {supportedUploadFileTypes}
                </strong>
              </p>
            )}
          >
            <Button
              variant="outlined"
              onClick={handleUploadMedia}
              disabled={disabled}
              color="secondary"
              className="!px-2 !py-1 !rounded-sm flex items-center gap-1"
            >
              <UploadIcon className="!w-3 !h-3" />
              Upload
            </Button>
          </ToolTip>
        </div>
        {enableTabFilter && (
          <TabFilter
            options={tabOptions}
            defaultTab={selectedCategory}
            onUpdate={setSelectedCategory}
            className="w-full"
            size="medium"
            theme="gray"
          />
        )}
      </div>
      <div className="relative flex flex-1 w-full overflow-y-hidden">
        <Table<MultiMediaResponse>
          instanceRef={tableInstanceRef as RefObject<VirtualTableInstance>}
          getRowId={(row) => row.mediaId}
          columns={fileColumns}
          getData={getPatientsMultimedia}
          queryParams={{
            patientId: patientId ?? 0,
            filterDTO: {
              mediaCategoryList: selectedCategory === 'All' ? [] : [selectedCategory],
            },
          }}
          onRowClick={(table, row) => {
            if (!isRowDisabled(row.original)) {
              handleRowSelect(row.original);
            }
          }}
          defaultPagination={{
            pageSize: 25,
            pageIndex: 0,
          }}
          className={clsx('!pb-0', classes?.table)}
          classes={{
            scrollbarGutter: 'stable',
            header: '!pr-4 !py-1 !border-t-0',
            outerRow: '!w-[calc(100%-16px)]',
            row: (row: Row<MultiMediaResponse>) => clsx({
              'bg-selected-entity text-black': isSelected(row.id) && !multiple && !isSelectionLoading,
              'opacity-50': isRowDisabled(row.original),
            }),
          }}
          rowProps={(row) => {
            const media = row.original;
            let tooltip: string | undefined;

            if (disabledMediaIdsSet.has(media.mediaId ?? 0)) {
              tooltip = disabledReason;
            } else if (isMimeTypeDisabled(media)) {
              tooltip = allowedMimeTypeReason;
            }

            return {
              tooltip,
              onDoubleClick: () => {
                if (!multiple && !isRowDisabled(media)) {
                  handleRowSelect(row.original);
                  onMediaDoubleClick?.(row.original.mediaId ?? 0);
                }
              },
            };
          }}
          loadingSlot="Loading multimedia..."
          emptySlot={(
            <Empty>
              <Button
                variant="outlined"
                onClick={handleUploadMedia}
                disabled={disabled}
                className="w-full !max-w-[initial] box-border flex !items-center !justify-center gap-2 rounded border !border-dashed !border-blue-500 transition-all !p-6 cursor-pointer !bg-blue-50 hover:!bg-blue-100 hover:!border-blue-400 mx-1"
              >
                <div className="flex flex-col gap-1">
                  <span className="font-semibold">Patient has no media yet.</span>
                  <div className="flex gap-1 !text-blue-500 items-center justify-center">
                    <UploadIcon className="!w-4 !h-4 !text-inherit" />
                    Add Files
                  </div>
                  <span className="text-xs font-normal text-gray-600 flex-1">
                    Supported file types:
                    {' '}
                    <span className="font-semibold">{supportedUploadFileTypes}</span>
                  </span>
                </div>
              </Button>
            </Empty>
          )}
        />
        {isBlockingResolve && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
            <div className="flex items-center gap-2 rounded-md bg-white px-3 py-2 shadow-sm">
              <Spinner className="!font-bold" size={24} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PatientMediaSelect({ multiple = true, value, ...props }: Props) {
  const selected = useMemo(
    () => {
      const result = Array.isArray(value) ? value : [value];
      return isNil(value) ? undefined : compact(result);
    },
    [value],
  );

  return (
    <SelectableProvider isSolo={!multiple} selected={selected}>
      <PatientMediaList multiple={multiple} value={value} {...props} />
    </SelectableProvider>
  );
}
